import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, CircleAlert, Clock3, Link2, LoaderCircle, Pause, Play, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import { apiFetch } from "@/lib/api";
import ChatConnections from "@/components/earnos/ChatConnections";
import WorkLiveBrowser from "@/components/earnos/WorkLiveBrowser";
import ArtifactPreview from "@/components/earnos/ArtifactPreview";

const TERMINAL = new Set(["completed", "completed_with_issues", "partially_completed", "failed", "cancelled"]);
const ATTENTION = new Set(["needs_input", "needs_connection", "awaiting_approval", "blocked"]);
const clean = value => String(value || "").replaceAll("_", " ");


export default function InlineWork({ missionId }) {
  const nav = useNavigate();
  const [work, setWork] = useState(null);
  const [error, setError] = useState("");
  const [acting, setActing] = useState(false);
  const [reviewed, setReviewed] = useState({});
  const [answer, setAnswer] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [activityOpen, setActivityOpen] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const lock = useRef(false);
  const refresh = useCallback(async () => {
    try {
      const result = await apiFetch("/mission/missions/" + encodeURIComponent(missionId));
      setWork(result);
      setLastChecked(new Date());
      setError("");
    } catch (e) { setError(e.message || "Work status unavailable"); }
  }, [missionId]);

  useEffect(() => {
    setWork(null); setReviewed({}); setExpanded(false);
    let active = true;
    const fetchOwned = async () => {
      try {
        const result = await apiFetch("/mission/missions/" + encodeURIComponent(missionId));
        if (active) { setWork(result); setLastChecked(new Date()); setError(""); }
      } catch (e) { if (active) setError(e.message || "Cannot load work"); }
    };
    fetchOwned();
    const timer = setInterval(fetchOwned, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [missionId]);

  async function takeAction(path, payload) {
    if (lock.current) return;
    lock.current = true; setActing(true); setError("");
    try {
      await apiFetch(path, {method: "POST", ...(payload ? {body: payload} : {})});
      await refresh();
      setReviewed({});
      setAnswer("");
    } catch (e) { setError(e.message || "Action not saved"); }
    finally { lock.current = false; setActing(false); }
  }

  if (!work) return <div className="mt-3 p-3 rounded-xl border border-line text-sm text-ink-2"
    data-testid="earnos-inline-work"><LoaderCircle className="inline mr-2 animate-spin" size={16}/>{error || "Loading saved work…"}</div>;
  const steps = work.tasks || [];
  const activity = (work.logs || []).slice(-12).reverse();
  const currentAction = steps.find(t => ["executing", "running"].includes(t.status)) ||
    steps.find(t => ["pending", "awaiting_approval"].includes(t.status));
  const currentStage = (work.stages || []).find(s => s.status === "active");
  const verified = steps.filter(x => x.status === "verified" && x.verified === true).length;
  const waiting = (work.approvals || []).filter(x => x.status === "pending");
  const stage = clean(work.stage || "understand");
  const complete = work.state === "completed";
  const stopped = TERMINAL.has(work.state);
  const doing = !stopped && !ATTENTION.has(work.state) && work.state !== "paused";
  const usageLimit = /\b429\b|monthly.*(?:limit|capacity)|(?:limit|capacity).*month/i.test(
    [work.attention, ...steps.map(t=>t.error)].filter(Boolean).join(" "));
  const externalGoal = work.objective_contract?.requires_external_proof;
  return <section className="mt-3 rounded-2xl border border-brand/35 bg-surface-3 p-3 sm:p-4 space-y-3"
    aria-label="Live EARNOS work in chat" data-testid="earnos-inline-work">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-xs uppercase font-bold text-brand-light tracking-wider">Your work · Live status</p>
        <p className="text-sm text-ink mt-1 font-semibold">{clean(work.state)} · {stage}</p>
      </div>
      <button type="button" aria-label="Refresh work" onClick={refresh} disabled={acting}
        className="shrink-0 p-2 rounded-xl border border-line text-ink-2"><RefreshCw size={16}/></button>
    </div>
    <div className="flex gap-2 items-start text-xs text-ink-2">
      {complete ? <CheckCircle2 size={17} className="text-ok shrink-0"/> :
        doing ? <LoaderCircle size={17} className="animate-spin text-brand-light shrink-0"/> :
        <CircleAlert size={17} className="text-brand-light shrink-0"/>}
      <span>{complete ? "All required checks for this mission passed." :
        work.attention || (doing ? "Working through the recorded stages. New evidence appears here automatically." :
        "This mission is not confirmed complete.")}</span>
    </div>
    <p className="text-xs text-ink-2">{verified} of {steps.length} tool tasks independently checked.
      {externalGoal ? " Documents and plans alone do not fulfill this goal." : ""}</p>
    <section aria-label="Work activity and progress" data-testid="earnos-work-live-activity"
      className="rounded-xl border border-line bg-surface-2 p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-bold text-ink flex items-center gap-2">
          {doing ? <LoaderCircle size={15} className="animate-spin text-brand-light"/> :
            <Clock3 size={15} className="text-ink-2"/>}
          {doing ? "EARNOS is working" : "Recorded work activity"}
        </p>
        <button type="button" onClick={()=>setActivityOpen(x=>!x)}
          className="text-xs font-semibold text-brand-light">
          {activityOpen ? "Hide activity" : "Show activity"}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-lg border border-line p-2">
          <p className="text-ink-2">Current stage</p>
          <p className="font-bold text-ink capitalize mt-1">{clean(currentStage?.name || work.stage || "understand")}</p>
        </div>
        <div className="rounded-lg border border-line p-2">
          <p className="text-ink-2">Next action</p>
          <p className="font-bold text-ink mt-1 break-words">
            {work.attention ? "Needs you" : currentAction ? currentAction.title :
              stopped ? "Review results" : "Checking next step"}
          </p>
        </div>
      </div>
      {activityOpen ? activity.length ?
        <ol className="space-y-3 max-h-64 overflow-y-auto" aria-label="Recorded EARNOS work events">
          {activity.slice(0,8).map((event,i) =>
            <li key={event.log_id || event.event_id || String(event.at) + "-" + i}
              className="flex gap-2 items-start text-xs">
              {event.level === "error" ? <CircleAlert size={15} className="text-amber-300 shrink-0 mt-0.5"/> :
               event.level === "attention" ? <ShieldCheck size={15} className="text-brand-light shrink-0 mt-0.5"/> :
               <CheckCircle2 size={15} className="text-ink-2 shrink-0 mt-0.5"/>}
              <div className="min-w-0 flex-1">
                <p className="text-ink break-words">{String(event.message || event.msg || "Step recorded").slice(0,220)}</p>
                <p className="text-ink-2 mt-0.5 capitalize">{clean(event.stage)}
                  {event.at ? " · " + new Date(event.at).toLocaleTimeString([], {hour:"numeric", minute:"2-digit"}) : ""}</p>
              </div>
            </li>)}
        </ol> :
        <p className="text-xs text-ink-2">No step log yet. EARNOS will show recorded actions and blockers as the mission runs.</p>
        : null}
      <p className="text-[11px] text-ink-2" role="status">
        {lastChecked ? "Status refreshed " + lastChecked.toLocaleTimeString([], {hour:"numeric", minute:"2-digit", second:"2-digit"}) : "Checking recorded work"}
        {" · "}Updates every 5 seconds while this chat is open.
      </p>
    </section>
    {work.browser_handoff?.session_id ?
      <WorkLiveBrowser key={work.browser_handoff.session_id}
        missionId={missionId} handoff={work.browser_handoff}/> : null}
    {usageLimit ? <div role="status" className="rounded-lg border border-amber-400/30 p-3 text-sm text-ink">
      AI usage limit reached. Work is saved but cannot continue until capacity is available.
      <button type="button" onClick={() => nav("/billing")} className="block text-brand-light font-bold mt-2">See plan and usage <ArrowRight size={14} className="inline"/></button>
    </div> : null}
    {work.state === "needs_connection" ? <div className="border border-brand/30 rounded-xl p-3 space-y-2">
      <p className="font-semibold text-sm text-ink">Connect your own account to continue</p>
      <p className="text-xs text-ink-2">Sign in through the approved provider or isolated browser. EARNOS never asks you to paste a password into chat.</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="rounded-xl bg-brand px-3 py-2 text-white text-xs font-bold" onClick={() => setConnectOpen(true)}><Link2 size={14} className="inline mr-1"/> Connect and sign in here</button>
        <button type="button" className="rounded-xl border border-line px-3 py-2 text-ink text-xs font-bold" onClick={() => nav("/browser-access?mission=" + encodeURIComponent(missionId))}>Allowed website sign-in</button>
      </div>
    </div> : null}
    {work.state === "needs_input" ? <div className="space-y-2">
      <p className="text-sm font-semibold text-ink">EARNOS needs one answer</p>
      {(work.questions || []).map((question, i) => <p key={i} className="text-sm text-ink-2">{question}</p>)}
      <textarea rows={2} value={answer} onChange={e => setAnswer(e.target.value)}
        className="w-full rounded-xl border border-line bg-surface-2 p-3 text-ink text-sm"
        aria-label="Answer EARNOS" placeholder="Your answer…" />
      <button type="button" disabled={acting || !answer.trim()} onClick={() =>
        takeAction("/mission/missions/" + encodeURIComponent(missionId) + "/answers",
          {answers: {clarification: answer.trim()}})}
        className="rounded-xl bg-brand text-white px-4 py-2 text-sm font-bold disabled:opacity-50">Continue work</button>
    </div> : null}
    {waiting.map(a => <div key={a.approval_id} className="rounded-xl border border-brand/40 p-3 space-y-2"
      data-testid="earnos-chat-approval">
      <p className="text-sm font-bold text-ink flex gap-2"><ShieldCheck size={16}/> Approval needed</p>
      <p className="text-sm text-ink">{a.action}</p>
      <p className="text-xs text-ink-2">Nothing outside your account changes unless you authorize the exact action.</p>
      <details><summary className="cursor-pointer text-xs font-bold text-brand-light">Review exact app, destination, and action</summary>
        <pre className="mt-2 p-2 max-h-40 overflow-auto rounded-lg bg-surface-2 text-xs whitespace-pre-wrap break-all text-ink-2">{JSON.stringify({tool:a.tool, args:a.args, preview:a.preview}, null, 2)}</pre>
      </details>
      <label className="flex gap-2 text-xs text-ink items-start"><input type="checkbox" checked={Boolean(reviewed[a.approval_id])}
        onChange={e=>setReviewed(x=>({...x,[a.approval_id]:e.target.checked}))}
        className="accent-brand mt-1"/> I reviewed the exact action and approve only this step.</label>
      <div className="flex gap-2 flex-wrap">
        <button type="button" disabled={acting || !reviewed[a.approval_id]}
          onClick={() => takeAction("/mission/approvals/" + encodeURIComponent(a.approval_id) + "/approve")}
          className="rounded-xl bg-brand text-white font-bold text-xs px-4 py-2 disabled:opacity-40">Approve this action</button>
        <button type="button" disabled={acting}
          onClick={() => takeAction("/mission/approvals/" + encodeURIComponent(a.approval_id) + "/decline")}
          className="rounded-xl border border-line font-bold text-xs px-4 py-2 text-ink">Decline</button>
      </div>
    </div>)}
    {(work.artifacts || []).length ? <div className="space-y-2">
      <p className="font-semibold text-sm text-ink">Results — open right here</p>
      {work.artifacts.map(a => <ArtifactPreview key={a.artifact_id} artifact={a} mission={work}/>)}
    </div> : null}
    {steps.length ? <div className="space-y-2">
      <button type="button" onClick={() => setExpanded(x=>!x)}
        className="text-xs font-bold text-brand-light">{expanded ? "Hide" : "Show"} execution steps ({steps.length})</button>
      {expanded ? steps.map(t => <div key={t.id} className="flex items-start gap-2 text-xs border-t border-line pt-2">
        {t.verified === true && t.status === "verified" ?
          <CheckCircle2 size={14} className="text-ok shrink-0 mt-0.5"/> : <Clock3 size={14} className="text-ink-2 shrink-0 mt-0.5"/>}
        <div className="min-w-0"><p className="font-semibold text-ink">{t.title}</p>
          <p className="text-ink-2">{clean(t.status)}{t.tool ? " · " + clean(t.tool) : ""}</p>
          {t.error ? <p className="text-amber-300 mt-1 break-words">{t.error}</p> : null}
          {t.verification ? <p className="text-ink-2 mt-1 break-words">{String(t.verification).slice(0,360)}</p> : null}
        </div>
      </div>) : null}
    </div> : null}
    {(work.income_evidence?.objective_kind === "verified_income_outcome") ? <p className="text-xs text-ink-2 border-t border-line pt-3">
      Real income: {work.income_evidence.income_proof_available ? "payment source checked" : "no independent customer payment confirmation yet"}.
    </p> : null}
    {error ? <p role="alert" className="text-xs text-danger">{error}</p> : null}
    <div className="flex gap-3 flex-wrap border-t border-line pt-3 items-center">
      <button type="button" onClick={() => nav("/mission?id=" + encodeURIComponent(missionId))}
        className="text-xs text-brand-light font-bold flex gap-1 items-center">
        Full work history <ArrowRight size={14}/>
      </button>
      {doing ? <button type="button" disabled={acting}
        onClick={() => takeAction("/mission/missions/" + encodeURIComponent(missionId) + "/pause")}
        className="text-xs font-bold text-ink flex gap-1 items-center disabled:opacity-50">
        <Pause size={14}/> Pause
      </button> : null}
      {work.state === "paused" ? <button type="button" disabled={acting}
        onClick={() => takeAction("/mission/missions/" + encodeURIComponent(missionId) + "/resume")}
        className="text-xs font-bold text-ink flex gap-1 items-center disabled:opacity-50">
        <Play size={14}/> Resume
      </button> : null}
      {!stopped ? <button type="button" disabled={acting}
        onClick={() => {
          if (window.confirm("Stop future EARNOS work on this job? An already-started outside action may need separate reconciliation.")) {
            takeAction("/mission/missions/" + encodeURIComponent(missionId) + "/cancel");
          }
        }}
        className="text-xs font-bold text-amber-300 flex gap-1 items-center disabled:opacity-50">
        <XCircle size={14}/> Stop job
      </button> : null}
    </div>
    {connectOpen ? <ChatConnections missionId={missionId} onClose={() => {
      setConnectOpen(false);
      refresh();
    }}/> : null}
  </section>;
}
